import { useState, useEffect, useCallback } from 'react';
import { DataTable } from '../../components/shared/DataTable';
import { BadgeModal } from '../../components/content/BadgeModal';
import { ConfirmDeleteModal } from '../../components/content/ConfirmDeleteModal';
import { searchBadges, deleteBadge } from '../../services/content/badgeService';
import { useRefresh } from '../../contexts/RefreshContext';
import { useAssociationLookup } from '../../hooks/useAssociationLookup';
import type { Badge } from '../../types/content/badge';
import type { Column } from '../../components/shared/DataTable';

function RowMenu({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 transition-colors">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[100px]">
          <button onClick={() => { setOpen(false); onEdit(); }} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Edit</button>
          <button onClick={() => { setOpen(false); onDelete(); }} className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50">Delete</button>
        </div>
      )}
    </div>
  );
}

export default function BadgesTab() {
  const { resolveAssociationName } = useAssociationLookup();
  const [data, setData] = useState<Badge[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalBadge, setModalBadge] = useState<Badge | undefined>(undefined);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const columns: Column<Badge>[] = [
    { key: 'name', label: 'Name' },
    { key: 'category', label: 'Category' },
    {
      key: 'association',
      label: 'Association',
      render: (item) => {
        if (!item.association) return '—';
        const name = resolveAssociationName(item.association, item.associationId);
        return name ? `${item.association}: ${name}` : item.association;
      },
    },
    {
      key: 'imageUrl',
      label: 'Image',
      render: (item) => item.imageUrl ? (
        <img src={item.imageUrl} alt={item.name} className="w-8 h-8 object-cover rounded" />
      ) : '—',
    },
  ];

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const results = await searchBadges({}, { page, pageSize, orderBy: 'updated', order: 'desc' });
      setData(results);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => { fetch(); }, [fetch]);

  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  useEffect(() => {
    registerRefreshHandler('/content/badges', fetch);
    return () => unregisterRefreshHandler('/content/badges');
  }, [fetch, registerRefreshHandler, unregisterRefreshHandler]);

  async function handleDelete(id: string) {
    await deleteBadge(id);
    setPendingDeleteId(null);
    fetch();
  }

  return (
    <>
      <DataTable
        title="Badges"
        columns={columns}
        data={data}
        page={page}
        pageSize={pageSize}
        loading={loading}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        onRefresh={fetch}
        onAdd={() => { setModalBadge(undefined); setShowModal(true); }}
        alignTop
        rowMenu={(item) => (
          <RowMenu
            onEdit={() => { setModalBadge(item); setShowModal(true); }}
            onDelete={() => setPendingDeleteId(item.id)}
          />
        )}
      />
      {pendingDeleteId && (
        <ConfirmDeleteModal
          title="Delete Badge"
          message="Are you sure you want to delete this badge?"
          onCancel={() => setPendingDeleteId(null)}
          onConfirm={() => handleDelete(pendingDeleteId)}
        />
      )}
      {showModal && (
        <BadgeModal
          badge={modalBadge}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); fetch(); }}
        />
      )}
    </>
  );
}
